/**
 * TKMasonry - A justified image gallery layout engine.
 * Mimics the behavior of JustifiedGallery by creating rows of images
 * that perfectly fill the container's width.
 */
export interface TKMasonryOptions {
    /** Selector for the gallery items */
    itemSelector: string;
    /** Margin between items in pixels */
    gap: number;
    /** The ideal height for each row */
    targetRowHeight: number;
    /** Behavior for the last row: 'justify', 'nojustify', or 'hide' */
    lastRowMode: 'justify' | 'nojustify' | 'hide';
    /** Whether to wait for all images to load before initial layout */
    waitForImages: boolean;
}

export default class TKMasonry {
    private root: HTMLElement;
    private options: TKMasonryOptions;
    private resizeObserver?: ResizeObserver;

    /**
     * @param root The container element
     * @param options Configuration options for the layout
     */
    constructor(root: HTMLElement, options: Partial<TKMasonryOptions> = {}) {
        this.root = root;
        this.options = {
            itemSelector: options.itemSelector || '.gallery-item',
            gap: options.gap ?? 10,
            targetRowHeight: options.targetRowHeight ?? 240,
            lastRowMode: options.lastRowMode || 'nojustify',
            waitForImages: options.waitForImages ?? true,
        };
    }

    /**
     * Initializes the gallery, binds events, and starts the layout process.
     */
    public async init(): Promise<void> {
        this.prepareRoot();

        if (this.options.waitForImages) {
            await this.waitForImages();
        }

        this.layout();
        this.bindEvents();
    }

    /**
     * Forces a re-calculation of the layout (e.g., after adding items).
     */
    public refresh(): void {
        this.layout();
    }

    /**
     * Cleans up observers and listeners.
     */
    public destroy(): void {
        this.resizeObserver?.disconnect();
    }

    private prepareRoot(): void {
        this.root.style.position = 'relative';
        this.root.style.width = '100%';
        this.root.style.display = 'block';
    }

    private bindEvents(): void {
        this.resizeObserver = new ResizeObserver(() => {
            requestAnimationFrame(() => this.layout());
        });
        this.resizeObserver.observe(this.root);
    }

    /**
     * Core layout logic. Groups items into rows and scales them to fit.
     */
    private layout(): void {
        const containerWidth = this.root.clientWidth;
        if (containerWidth <= 0) return;

        const items = Array.from(this.root.querySelectorAll<HTMLElement>(this.options.itemSelector));
        const rows: HTMLElement[][] = [];
        let currentRow: HTMLElement[] = [];
        let currentRowAspectRatio = 0;

        // Step 1: Group items into rows based on aspect ratios
        items.forEach((item) => {
            const ratio = this.getItemAspectRatio(item);
            const rowGaps = currentRow.length * this.options.gap;

            // Check if adding this item exceeds the container width
            if (
                currentRow.length > 0 &&
                (currentRowAspectRatio + ratio) * this.options.targetRowHeight + rowGaps > containerWidth
            ) {
                rows.push(currentRow);
                currentRow = [item];
                currentRowAspectRatio = ratio;
            } else {
                currentRow.push(item);
                currentRowAspectRatio += ratio;
            }
        });

        if (currentRow.length > 0) {
            rows.push(currentRow);
        }

        // Step 2: Render rows
        let currentY = 0;

        rows.forEach((row, index) => {
            const isLastRow = index === rows.length - 1;
            const totalGaps = (row.length - 1) * this.options.gap;
            const availableWidth = containerWidth - totalGaps;

            const rowAspectRatio = row.reduce((sum, item) => sum + this.getItemAspectRatio(item), 0);

            let shouldJustify = true;

            if (isLastRow) {
                if (this.options.lastRowMode === 'hide') {
                    row.forEach((item) => (item.style.display = 'none'));
                    return;
                }
                shouldJustify = this.options.lastRowMode === 'justify';
            }

            const rowHeight = shouldJustify ? availableWidth / rowAspectRatio : this.options.targetRowHeight;

            let currentX = 0;
            row.forEach((item, itemIndex) => {
                const isLastInRow = itemIndex === row.length - 1;
                const ratio = this.getItemAspectRatio(item);

                let width: number;

                if (shouldJustify && isLastInRow) {
                    width = containerWidth - currentX;
                } else {
                    width = Math.floor(rowHeight * ratio);
                }

                this.applyItemStyles(item, currentX, currentY, width, rowHeight);

                currentX += width + this.options.gap;
            });

            currentY += rowHeight + this.options.gap;
        });

        this.root.style.height = `${currentY - this.options.gap}px`;
    }

    private getItemAspectRatio(item: HTMLElement): number {
        const img = item.querySelector('img');
        if (img) {
            const width = img.naturalWidth || img.width;
            const height = img.naturalHeight || img.height;

            if (width > 0 && height > 0) {
                return width / height;
            }
        }

        return 1.0; // Fallback for missing images or non-image items
    }

    private applyItemStyles(item: HTMLElement, x: number, y: number, w: number, h: number): void {
        item.style.position = 'absolute';
        item.style.left = '0';
        item.style.top = '0';
        item.style.width = `${w}px`;
        item.style.height = `${h}px`;
        item.style.transform = `translate3d(${x}px, ${y}px, 0)`;
        item.style.display = 'block';

        const img = item.querySelector('img');
        if (img) {
            img.style.width = '100%';
            img.style.height = '100%';
            img.style.objectFit = 'cover';
        }
    }

    private async waitForImages(): Promise<void> {
        const images = Array.from(this.root.querySelectorAll<HTMLImageElement>('img'));
        const promises = images.map((img) => {
            if (img.complete && img.naturalWidth > 0) {
                return Promise.resolve();
            }

            return new Promise<void>((resolve) => {
                const done = () => {
                    img.removeEventListener('load', done);
                    img.removeEventListener('error', done);
                    resolve();
                };

                img.addEventListener('load', done, { once: true });
                img.addEventListener('error', done, { once: true });
            });
        });

        await Promise.all(promises);
    }
}
